// Curve smoothing — Catmull-Rom spline (tension 0.5) as cubic-bezier `C` segments,
// endpoints duplicated (p0←p1, p3←p2) so the first/last spans stay anchored. Factor
// 1/6 = t/3 at t=0.5. The MATH is identical to Claude Design's; the two standalones
// only differ in how they SERIALISE the `d` string, so we ship both ports verbatim:
//
//   smoothPath      — §III `smooth()`: coords comma-joined, points space-joined
//                     (`M x,y C cx,cy cx,cy x,y`).
//   smoothPathCover — §I  `smoothPath()`: coords space-joined, points comma-joined
//                     (`M x y C cx cy, cx cy, x y`).
//
// Same curve either way; keeping each section byte-identical to its own canon is what
// lets the SVG parity test compare `d` strings, not just geometry.

export function smoothPath(points, t = 0.5) {
  if (points.length < 2) return points.length ? `M ${points[0][0].toFixed(2)},${points[0][1].toFixed(2)}` : '';
  const f = (n) => n.toFixed(2);
  let d = `M ${f(points[0][0])},${f(points[0][1])}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const c1x = p1[0] + (p2[0] - p0[0]) * (t / 3);
    const c1y = p1[1] + (p2[1] - p0[1]) * (t / 3);
    const c2x = p2[0] - (p3[0] - p1[0]) * (t / 3);
    const c2y = p2[1] - (p3[1] - p1[1]) * (t / 3);
    d += ` C ${f(c1x)},${f(c1y)} ${f(c2x)},${f(c2y)} ${f(p2[0])},${f(p2[1])}`;
  }
  return d;
}

// §I silhouette serialisation — VERBATIM from the R10 §I standalone `smoothPath()`,
// including its two short-circuits: <2 points → empty, exactly 2 → a straight `L`
// segment with RAW (un-rounded) coords. Only 3+ points run through the spline.
export function smoothPathCover(points, t = 0.5) {
  if (points.length < 2) return '';
  if (points.length === 2) return `M ${points[0][0]} ${points[0][1]} L ${points[1][0]} ${points[1][1]}`;
  const f = (n) => n.toFixed(2);
  let d = `M ${f(points[0][0])} ${f(points[0][1])}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const c1x = p1[0] + (p2[0] - p0[0]) * (t / 3);
    const c1y = p1[1] + (p2[1] - p0[1]) * (t / 3);
    const c2x = p2[0] - (p3[0] - p1[0]) * (t / 3);
    const c2y = p2[1] - (p3[1] - p1[1]) * (t / 3);
    d += ` C ${f(c1x)} ${f(c1y)}, ${f(c2x)} ${f(c2y)}, ${f(p2[0])} ${f(p2[1])}`;
  }
  return d;
}
